import React from 'react';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/pageTranslations';

// Template para páginas de FAQ
const FAQPage = ({ titulo, contenido, backText, ctaText }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion/faq')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{backText}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-6">
            {titulo}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-ivory p-8 rounded-none shadow-lg mb-8">
            {contenido}
          </div>

          <div className="text-center">
            <Button 
              onClick={() => navigate('/rinomodelacion/contacto')}
              className="bg-gold hover:bg-gold text-white px-8 py-6 text-lg rounded-none"
            >
              {ctaText}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

// Páginas individuales de FAQ
export const FAQProcedimiento = () => {
  const lang = useLanguage();
  
  const texts = {
    es: {
      backText: 'Volver a Preguntas Frecuentes',
      ctaText: '¿Más preguntas? Contáctanos',
      titulo: '¿Cómo es el procedimiento de Rinomodelación con el Dr. Ángel Ruiz Norro?',
      intro: 'El procedimiento de rinomodelación con el Dr. Norro es sencillo, seguro y prácticamente indoloro.',
      stepsTitle: 'Pasos del procedimiento:',
      steps: [
        { title: 'Consulta inicial', desc: 'Se realiza un estudio de tu nariz y se definen los objetivos del tratamiento.' },
        { title: 'Preparación', desc: 'Se aplica anestesia tópica para garantizar tu comodidad durante el procedimiento.' },
        { title: 'Aplicación', desc: 'El Dr. Norro aplica el material de relleno con precisión milimétrica. Dura entre 15-30 minutos.' },
        { title: 'Resultado inmediato', desc: 'Los cambios son visibles al instante. Puedes volver a tu rutina inmediatamente.' },
      ],
      benefitsTitle: 'Ventajas del procedimiento:',
      benefits: ['Sin cirugía ni quirófano', 'Sin anestesia general', 'Recuperación inmediata', 'Sin hematomas ni vendajes'],
    },
    en: {
      backText: 'Back to FAQ',
      ctaText: 'More questions? Contact us',
      titulo: 'How is the Rhinomodeling procedure with Dr. Ángel Ruiz Norro?',
      intro: 'The rhinomodeling procedure with Dr. Norro is simple, safe and virtually painless.',
      stepsTitle: 'Procedure steps:',
      steps: [
        { title: 'Initial consultation', desc: 'A study of your nose is performed and treatment goals are defined.' },
        { title: 'Preparation', desc: 'Topical anesthesia is applied to ensure your comfort during the procedure.' },
        { title: 'Application', desc: 'Dr. Norro applies the filler material with millimetric precision. It takes 15-30 minutes.' },
        { title: 'Immediate result', desc: 'Changes are visible instantly. You can return to your routine immediately.' },
      ],
      benefitsTitle: 'Procedure advantages:',
      benefits: ['No surgery or operating room', 'No general anesthesia', 'Immediate recovery', 'No bruising or bandages'],
    },
    fr: {
      backText: 'Retour aux FAQ',
      ctaText: 'Plus de questions? Contactez-nous',
      titulo: 'Comment se déroule la procédure de Rhinomodelage avec le Dr. Ángel Ruiz Norro?',
      intro: 'La procédure de rhinomodelage avec le Dr. Norro est simple, sûre et pratiquement indolore.',
      stepsTitle: 'Étapes de la procédure:',
      steps: [
        { title: 'Consultation initiale', desc: 'Une étude de votre nez est réalisée et les objectifs du traitement sont définis.' },
        { title: 'Préparation', desc: 'Une anesthésie topique est appliquée pour assurer votre confort pendant la procédure.' },
        { title: 'Application', desc: 'Le Dr. Norro applique le produit de comblement avec une précision millimétrique. Cela prend 15-30 minutes.' },
        { title: 'Résultat immédiat', desc: 'Les changements sont visibles instantanément. Vous pouvez reprendre votre routine immédiatement.' },
      ],
      benefitsTitle: 'Avantages de la procédure:',
      benefits: ['Sans chirurgie ni bloc opératoire', 'Sans anesthésie générale', 'Récupération immédiate', 'Sans hématomes ni pansements'],
    },
    pt: {
      backText: 'Voltar para Perguntas Frequentes',
      ctaText: 'Mais perguntas? Contate-nos',
      titulo: 'Como é o procedimento de Rinomodelação com o Dr. Ángel Ruiz Norro?',
      intro: 'O procedimento de rinomodelação com o Dr. Norro é simples, seguro e praticamente indolor.',
      stepsTitle: 'Etapas do procedimento:',
      steps: [
        { title: 'Consulta inicial', desc: 'É realizado um estudo do seu nariz e são definidos os objetivos do tratamento.' },
        { title: 'Preparação', desc: 'É aplicada anestesia tópica para garantir seu conforto durante o procedimento.' },
        { title: 'Aplicação', desc: 'O Dr. Norro aplica o material de preenchimento com precisão milimétrica. Dura entre 15-30 minutos.' },
        { title: 'Resultado imediato', desc: 'As mudanças são visíveis instantaneamente. Você pode voltar à sua rotina imediatamente.' },
      ],
      benefitsTitle: 'Vantagens do procedimento:',
      benefits: ['Sem cirurgia nem centro cirúrgico', 'Sem anestesia geral', 'Recuperação imediata', 'Sem hematomas nem curativos'],
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <FAQPage 
      backText={t.backText}
      ctaText={t.ctaText}
      titulo={t.titulo}
      contenido={
        <div className="prose max-w-none">
          <p className="text-lg text-text-secondary leading-relaxed mb-6">
            {t.intro}
          </p>
          
          <h3 className="text-xl font-semibold text-[#1C1C1A] mb-4">{t.stepsTitle}</h3>
          
          <ol className="space-y-4 mb-6">
            {t.steps.map((step, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="w-8 h-8 bg-gold/10 rounded-none flex items-center justify-center flex-shrink-0 text-gold font-semibold">{index + 1}</span>
                <div>
                  <h4 className="font-semibold text-[#1C1C1A]">{step.title}</h4>
                  <p className="text-text-secondary">{step.desc}</p>
                </div>
              </li>
            ))}
          </ol>
          
          <div className="bg-cream p-6 rounded-none">
            <h4 className="font-semibold text-[#1C1C1A] mb-3">{t.benefitsTitle}</h4>
            <ul className="space-y-2">
              {t.benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-2 text-text-secondary">
                  <CheckCircle className="text-gold" size={18} />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      }
    />
  );
};

export const FAQEstudioImagen = () => {
  const lang = useLanguage();
  
  const texts = {
    es: {
      backText: 'Volver a Preguntas Frecuentes',
      ctaText: '¿Más preguntas? Contáctanos',
      titulo: '¿En qué consiste y cómo se realiza el estudio de la imagen?',
      intro: 'El estudio de imagen es una herramienta fundamental que utilizamos para planificar tu tratamiento y mostrarte los posibles resultados antes de realizarlo.',
      howTitle: '¿Cómo funciona?',
      steps: [
        { num: '1', text: 'Fotografías profesionales:', desc: 'Tomamos fotografías de tu rostro desde diferentes ángulos (frente, perfil, tres cuartos).' },
        { num: '2', text: 'Análisis facial:', desc: 'El Dr. Norro analiza las proporciones de tu rostro y las características de tu nariz.' },
        { num: '3', text: 'Simulación digital:', desc: 'Mediante software especializado, simulamos los posibles resultados del tratamiento.' },
        { num: '4', text: 'Planificación:', desc: 'Juntos definimos los objetivos realistas y el plan de tratamiento personalizado.' },
      ],
      featuresTitle: 'Este estudio es:',
      features: [
        { title: 'Consulta Personalizada', desc: 'Valoración individualizada' },
        { title: 'Sin compromiso', desc: 'No obliga a realizar ningún tratamiento' },
        { title: 'Personalizado', desc: 'Adaptado a tu caso específico' },
      ],
    },
    en: {
      backText: 'Back to FAQ',
      ctaText: 'More questions? Contact us',
      titulo: 'What does the image study consist of and how is it performed?',
      intro: 'The image study is a fundamental tool we use to plan your treatment and show you possible results before performing it.',
      howTitle: 'How does it work?',
      steps: [
        { num: '1', text: 'Professional photographs:', desc: 'We take photographs of your face from different angles (front, profile, three-quarters).' },
        { num: '2', text: 'Facial analysis:', desc: 'Dr. Norro analyzes the proportions of your face and the characteristics of your nose.' },
        { num: '3', text: 'Digital simulation:', desc: 'Using specialized software, we simulate the possible results of the treatment.' },
        { num: '4', text: 'Planning:', desc: 'Together we define realistic goals and the personalized treatment plan.' },
      ],
      featuresTitle: 'This study is:',
      features: [
        { title: 'Personalized Consultation', desc: 'Individualized assessment' },
        { title: 'No commitment', desc: 'Does not oblige any treatment' },
        { title: 'Personalized', desc: 'Adapted to your specific case' },
      ],
    },
    fr: {
      backText: 'Retour aux FAQ',
      ctaText: 'Plus de questions? Contactez-nous',
      titulo: 'En quoi consiste l\'étude d\'image et comment est-elle réalisée?',
      intro: 'L\'étude d\'image est un outil fondamental que nous utilisons pour planifier votre traitement et vous montrer les résultats possibles avant de le réaliser.',
      howTitle: 'Comment ça fonctionne?',
      steps: [
        { num: '1', text: 'Photographies professionnelles:', desc: 'Nous prenons des photos de votre visage sous différents angles (face, profil, trois-quarts).' },
        { num: '2', text: 'Analyse faciale:', desc: 'Le Dr. Norro analyse les proportions de votre visage et les caractéristiques de votre nez.' },
        { num: '3', text: 'Simulation numérique:', desc: 'À l\'aide d\'un logiciel spécialisé, nous simulons les résultats possibles du traitement.' },
        { num: '4', text: 'Planification:', desc: 'Ensemble, nous définissons des objectifs réalistes et le plan de traitement personnalisé.' },
      ],
      featuresTitle: 'Cette étude est:',
      features: [
        { title: 'Consultation Personnalisée', desc: 'Évaluation individualisée' },
        { title: 'Sans engagement', desc: 'N\'oblige à aucun traitement' },
        { title: 'Personnalisé', desc: 'Adapté à votre cas spécifique' },
      ],
    },
    pt: {
      backText: 'Voltar para Perguntas Frequentes',
      ctaText: 'Mais perguntas? Contate-nos',
      titulo: 'Em que consiste e como é realizado o estudo de imagem?',
      intro: 'O estudo de imagem é uma ferramenta fundamental que utilizamos para planejar seu tratamento e mostrar os possíveis resultados antes de realizá-lo.',
      howTitle: 'Como funciona?',
      steps: [
        { num: '1', text: 'Fotografias profissionais:', desc: 'Tiramos fotografias do seu rosto de diferentes ângulos (frente, perfil, três quartos).' },
        { num: '2', text: 'Análise facial:', desc: 'O Dr. Norro analisa as proporções do seu rosto e as características do seu nariz.' },
        { num: '3', text: 'Simulação digital:', desc: 'Usando software especializado, simulamos os possíveis resultados do tratamento.' },
        { num: '4', text: 'Planejamento:', desc: 'Juntos definimos objetivos realistas e o plano de tratamento personalizado.' },
      ],
      featuresTitle: 'Este estudo é:',
      features: [
        { title: 'Consulta Personalizada', desc: 'Avaliação individualizada' },
        { title: 'Sem compromisso', desc: 'Não obriga a nenhum tratamento' },
        { title: 'Personalizado', desc: 'Adaptado ao seu caso específico' },
      ],
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <FAQPage 
      backText={t.backText}
      ctaText={t.ctaText}
      titulo={t.titulo}
      contenido={
        <div className="prose max-w-none">
          <p className="text-lg text-text-secondary leading-relaxed mb-6">
            {t.intro}
          </p>
          
          <h3 className="text-xl font-semibold text-[#1C1C1A] mb-4">{t.howTitle}</h3>
          
          <div className="space-y-4 mb-6">
            {t.steps.map((step, index) => (
              <p key={index} className="text-text-secondary">
                <strong>{step.num}. {step.text}</strong> {step.desc}
              </p>
            ))}
          </div>
          
          <div className="bg-cream p-6 rounded-none">
            <h4 className="font-semibold text-[#1C1C1A] mb-3">{t.featuresTitle}</h4>
            <ul className="space-y-2">
              {t.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-text-secondary">
                  <CheckCircle className="text-gold" size={18} />
                  <span><strong>{feature.title}</strong> - {feature.desc}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      }
    />
  );
};

export const FAQPrecios = () => {
  const lang = useLanguage();
  
  const texts = {
    es: {
      backText: 'Volver a Preguntas Frecuentes',
      ctaText: '¿Más preguntas? Contáctanos',
      titulo: '¿Cuánto cuesta la Rinomodelación con el Dr. Ángel Ruiz Norro?',
      intro: 'El precio de la rinomodelación varía según las necesidades específicas de cada paciente y el tipo de tratamiento elegido.',
      factorsTitle: 'Factores que influyen en el precio:',
      factors: [
        { title: 'Tipo de tratamiento:', desc: 'Temporal (ácido hialurónico) o permanente (hidroxiapatita cálcica)' },
        { title: 'Cantidad de material:', desc: 'Depende de las correcciones necesarias en cada caso' },
        { title: 'Complejidad del caso:', desc: 'Casos simples vs. correcciones más complejas' },
      ],
      budgetTitle: 'Presupuesto personalizado',
      budgetText: 'Durante su consulta, el Dr. Norro evaluará su caso y le proporcionará un presupuesto detallado y personalizado.',
      paymentTitle: 'Facilidades de pago:',
      payments: ['Financiación disponible', 'Pago fraccionado sin intereses', 'Tarjeta de crédito, transferencia o efectivo'],
    },
    en: {
      backText: 'Back to FAQ',
      ctaText: 'More questions? Contact us',
      titulo: 'How much does Rhinomodeling cost with Dr. Ángel Ruiz Norro?',
      intro: 'The price of rhinomodeling varies according to the specific needs of each patient and the type of treatment chosen.',
      factorsTitle: 'Factors that influence the price:',
      factors: [
        { title: 'Type of treatment:', desc: 'Temporary (hyaluronic acid) or permanent (calcium hydroxyapatite)' },
        { title: 'Amount of material:', desc: 'Depends on the corrections needed in each case' },
        { title: 'Case complexity:', desc: 'Simple cases vs. more complex corrections' },
      ],
      budgetTitle: 'Personalized budget',
      budgetText: 'During your consultation, Dr. Norro will evaluate your case and provide you with a detailed and personalized quote.',
      paymentTitle: 'Payment facilities:',
      payments: ['Financing available', 'Interest-free installments', 'Credit card, transfer or cash'],
    },
    fr: {
      backText: 'Retour aux FAQ',
      ctaText: 'Plus de questions? Contactez-nous',
      titulo: 'Combien coûte le Rhinomodelage avec le Dr. Ángel Ruiz Norro?',
      intro: 'Le prix du rhinomodelage varie selon les besoins spécifiques de chaque patient et le type de traitement choisi.',
      factorsTitle: 'Facteurs qui influencent le prix:',
      factors: [
        { title: 'Type de traitement:', desc: 'Temporaire (acide hyaluronique) ou permanent (hydroxyapatite de calcium)' },
        { title: 'Quantité de matériau:', desc: 'Dépend des corrections nécessaires dans chaque cas' },
        { title: 'Complexité du cas:', desc: 'Cas simples vs. corrections plus complexes' },
      ],
      budgetTitle: 'Devis personnalisé',
      budgetText: 'Lors de votre consultation, le Dr. Norro évaluera votre cas et vous fournira un devis détaillé et personnalisé.',
      paymentTitle: 'Facilités de paiement:',
      payments: ['Financement disponible', 'Paiement échelonné sans intérêts', 'Carte de crédit, virement ou espèces'],
    },
    pt: {
      backText: 'Voltar para Perguntas Frequentes',
      ctaText: 'Mais perguntas? Contate-nos',
      titulo: 'Quanto custa a Rinomodelação com o Dr. Ángel Ruiz Norro?',
      intro: 'O preço da rinomodelação varia de acordo com as necessidades específicas de cada paciente e o tipo de tratamento escolhido.',
      factorsTitle: 'Fatores que influenciam o preço:',
      factors: [
        { title: 'Tipo de tratamento:', desc: 'Temporário (ácido hialurônico) ou permanente (hidroxiapatita de cálcio)' },
        { title: 'Quantidade de material:', desc: 'Depende das correções necessárias em cada caso' },
        { title: 'Complexidade do caso:', desc: 'Casos simples vs. correções mais complexas' },
      ],
      budgetTitle: 'Orçamento personalizado',
      budgetText: 'Durante sua consulta, o Dr. Norro avaliará seu caso e fornecerá um orçamento detalhado e personalizado.',
      paymentTitle: 'Facilidades de pagamento:',
      payments: ['Financiamento disponível', 'Pagamento parcelado sem juros', 'Cartão de crédito, transferência ou dinheiro'],
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <FAQPage 
      backText={t.backText}
      ctaText={t.ctaText}
      titulo={t.titulo}
      contenido={
        <div className="prose max-w-none">
          <p className="text-lg text-text-secondary leading-relaxed mb-6">
            {t.intro}
          </p>
          
          <h3 className="text-xl font-semibold text-[#1C1C1A] mb-4">{t.factorsTitle}</h3>
          
          <ul className="space-y-3 mb-6">
            {t.factors.map((factor, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="text-gold mt-1 flex-shrink-0" size={20} />
                <div>
                  <strong className="text-[#1C1C1A]">{factor.title}</strong>
                  <p className="text-text-secondary">{factor.desc}</p>
                </div>
              </li>
            ))}
          </ul>
          
          <div className="bg-gradient-to-r from-cream0 to-blue-500 text-white p-6 rounded-none mb-6">
            <h4 className="font-semibold mb-3 text-lg">{t.budgetTitle}</h4>
            <p>{t.budgetText}</p>
          </div>
          
          <div className="bg-cream p-6 rounded-none">
            <h4 className="font-semibold text-[#1C1C1A] mb-3">{t.paymentTitle}</h4>
            <ul className="space-y-2">
              {t.payments.map((payment, index) => (
                <li key={index} className="flex items-center gap-2 text-text-secondary">
                  <CheckCircle className="text-gold" size={18} />
                  <span>{payment}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      }
    />
  );
};

export const FAQMateriales = () => {
  const lang = useLanguage();
  
  const texts = {
    es: {
      backText: 'Volver a Preguntas Frecuentes',
      ctaText: '¿Más preguntas? Contáctanos',
      titulo: '¿Qué materiales y qué técnica usa el Dr. Ángel Ruiz Norro en la Rinomodelación?',
      intro: 'El Dr. Norro utiliza exclusivamente materiales de la más alta calidad, aprobados por las autoridades sanitarias y con amplia experiencia clínica.',
      hyaluronic: { title: 'Ácido Hialurónico', subtitle: 'Para resultados temporales (12-18 meses)', features: ['Biocompatible y reversible', 'Ideal para probar el resultado', 'Mínimo riesgo'] },
      hydroxy: { title: 'Hidroxiapatita Cálcica', subtitle: 'Para resultados permanentes', features: ['Bioestimulador natural', 'Resultados definitivos', 'Integración tisular completa'] },
      techniqueTitle: 'Técnica del Dr. Norro:',
      techniqueIntro: 'El Dr. Norro ha desarrollado una técnica propia basada en más de 25 años de experiencia que incluye:',
      techniquePoints: ['Uso de microcánulas para minimizar hematomas', 'Aplicación en múltiples planos para resultados naturales', 'Modelado artístico respetando la armonía facial'],
    },
    en: {
      backText: 'Back to FAQ',
      ctaText: 'More questions? Contact us',
      titulo: 'What materials and technique does Dr. Ángel Ruiz Norro use in Rhinomodeling?',
      intro: 'Dr. Norro exclusively uses the highest quality materials, approved by health authorities and with extensive clinical experience.',
      hyaluronic: { title: 'Hyaluronic Acid', subtitle: 'For temporary results (12-18 months)', features: ['Biocompatible and reversible', 'Ideal for testing the result', 'Minimal risk'] },
      hydroxy: { title: 'Calcium Hydroxyapatite', subtitle: 'For permanent results', features: ['Natural biostimulator', 'Definitive results', 'Complete tissue integration'] },
      techniqueTitle: 'Dr. Norro\'s Technique:',
      techniqueIntro: 'Dr. Norro has developed his own technique based on more than 25 years of experience that includes:',
      techniquePoints: ['Use of microcannulas to minimize bruising', 'Multi-plane application for natural results', 'Artistic modeling respecting facial harmony'],
    },
    fr: {
      backText: 'Retour aux FAQ',
      ctaText: 'Plus de questions? Contactez-nous',
      titulo: 'Quels matériaux et quelle technique le Dr. Ángel Ruiz Norro utilise-t-il en Rhinomodelage?',
      intro: 'Le Dr. Norro utilise exclusivement des matériaux de la plus haute qualité, approuvés par les autorités sanitaires et avec une vaste expérience clinique.',
      hyaluronic: { title: 'Acide Hyaluronique', subtitle: 'Pour des résultats temporaires (12-18 mois)', features: ['Biocompatible et réversible', 'Idéal pour tester le résultat', 'Risque minimal'] },
      hydroxy: { title: 'Hydroxyapatite de Calcium', subtitle: 'Pour des résultats permanents', features: ['Biostimulateur naturel', 'Résultats définitifs', 'Intégration tissulaire complète'] },
      techniqueTitle: 'Technique du Dr. Norro:',
      techniqueIntro: 'Le Dr. Norro a développé sa propre technique basée sur plus de 25 ans d\'expérience qui comprend:',
      techniquePoints: ['Utilisation de microcanules pour minimiser les hématomes', 'Application multi-plans pour des résultats naturels', 'Modelage artistique respectant l\'harmonie faciale'],
    },
    pt: {
      backText: 'Voltar para Perguntas Frequentes',
      ctaText: 'Mais perguntas? Contate-nos',
      titulo: 'Quais materiais e técnica o Dr. Ángel Ruiz Norro usa na Rinomodelação?',
      intro: 'O Dr. Norro utiliza exclusivamente materiais da mais alta qualidade, aprovados pelas autoridades sanitárias e com ampla experiência clínica.',
      hyaluronic: { title: 'Ácido Hialurônico', subtitle: 'Para resultados temporários (12-18 meses)', features: ['Biocompatível e reversível', 'Ideal para testar o resultado', 'Risco mínimo'] },
      hydroxy: { title: 'Hidroxiapatita de Cálcio', subtitle: 'Para resultados permanentes', features: ['Bioestimulador natural', 'Resultados definitivos', 'Integração tecidual completa'] },
      techniqueTitle: 'Técnica do Dr. Norro:',
      techniqueIntro: 'O Dr. Norro desenvolveu sua própria técnica baseada em mais de 25 anos de experiência que inclui:',
      techniquePoints: ['Uso de microcânulas para minimizar hematomas', 'Aplicação em múltiplos planos para resultados naturais', 'Modelagem artística respeitando a harmonia facial'],
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <FAQPage 
      backText={t.backText}
      ctaText={t.ctaText}
      titulo={t.titulo}
      contenido={
        <div className="prose max-w-none">
          <p className="text-lg text-text-secondary leading-relaxed mb-6">
            {t.intro}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-cream p-6 rounded-none">
              <h4 className="font-semibold text-[#1C1C1A] mb-3">{t.hyaluronic.title}</h4>
              <p className="text-text-secondary mb-3">{t.hyaluronic.subtitle}</p>
              <ul className="space-y-1 text-sm text-text-secondary">
                {t.hyaluronic.features.map((f, i) => <li key={i}>• {f}</li>)}
              </ul>
            </div>
            
            <div className="bg-cream p-6 rounded-none">
              <h4 className="font-semibold text-[#1C1C1A] mb-3">{t.hydroxy.title}</h4>
              <p className="text-text-secondary mb-3">{t.hydroxy.subtitle}</p>
              <ul className="space-y-1 text-sm text-text-secondary">
                {t.hydroxy.features.map((f, i) => <li key={i}>• {f}</li>)}
              </ul>
            </div>
          </div>
          
          <h3 className="text-xl font-semibold text-[#1C1C1A] mb-4">{t.techniqueTitle}</h3>
          <p className="text-text-secondary mb-4">{t.techniqueIntro}</p>
          <ul className="space-y-2 mb-6">
            {t.techniquePoints.map((point, index) => (
              <li key={index} className="flex items-center gap-2 text-text-secondary">
                <CheckCircle className="text-gold" size={18} />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      }
    />
  );
};

export const FAQDiferencias = () => {
  const lang = useLanguage();
  
  const texts = {
    es: {
      backText: 'Volver a Preguntas Frecuentes',
      ctaText: '¿Más preguntas? Contáctanos',
      titulo: '¿Cuál es la diferencia entre Rinomodelación y Rinoplastia?',
      intro: 'Aunque ambos procedimientos buscan mejorar la apariencia de la nariz, son técnicas muy diferentes.',
      tableHeaders: ['Aspecto', 'Rinomodelación', 'Rinoplastia'],
      tableRows: [
        ['Tipo', 'No quirúrgico', 'Quirúrgico'],
        ['Anestesia', 'Local tópica', 'General'],
        ['Duración', '15-30 minutos', '1-3 horas'],
        ['Recuperación', 'Inmediata', '2-4 semanas'],
        ['Resultados', 'Inmediatos', '6-12 meses'],
        ['Riesgos', 'Mínimos', 'Mayores (cirugía)'],
      ],
      chooseTitle: '¿Cuándo elegir cada opción?',
      rinomodelacion: 'Rinomodelación: Ideal para correcciones estéticas que no requieren reducir el tamaño de la nariz (gibas, asimetrías, punta caída).',
      rinoplastia: 'Rinoplastia: Necesaria cuando se requiere reducir el tamaño de la nariz o corregir problemas funcionales.',
    },
    en: {
      backText: 'Back to FAQ',
      ctaText: 'More questions? Contact us',
      titulo: 'What is the difference between Rhinomodeling and Rhinoplasty?',
      intro: 'Although both procedures seek to improve the appearance of the nose, they are very different techniques.',
      tableHeaders: ['Aspect', 'Rhinomodeling', 'Rhinoplasty'],
      tableRows: [
        ['Type', 'Non-surgical', 'Surgical'],
        ['Anesthesia', 'Topical local', 'General'],
        ['Duration', '15-30 minutes', '1-3 hours'],
        ['Recovery', 'Immediate', '2-4 weeks'],
        ['Results', 'Immediate', '6-12 months'],
        ['Risks', 'Minimal', 'Higher (surgery)'],
      ],
      chooseTitle: 'When to choose each option?',
      rinomodelacion: 'Rhinomodeling: Ideal for aesthetic corrections that do not require reducing the size of the nose (humps, asymmetries, drooping tip).',
      rinoplastia: 'Rhinoplasty: Necessary when reducing the size of the nose or correcting functional problems is required.',
    },
    fr: {
      backText: 'Retour aux FAQ',
      ctaText: 'Plus de questions? Contactez-nous',
      titulo: 'Quelle est la différence entre Rhinomodelage et Rhinoplastie?',
      intro: 'Bien que les deux procédures visent à améliorer l\'apparence du nez, ce sont des techniques très différentes.',
      tableHeaders: ['Aspect', 'Rhinomodelage', 'Rhinoplastie'],
      tableRows: [
        ['Type', 'Non chirurgical', 'Chirurgical'],
        ['Anesthésie', 'Locale topique', 'Générale'],
        ['Durée', '15-30 minutes', '1-3 heures'],
        ['Récupération', 'Immédiate', '2-4 semaines'],
        ['Résultats', 'Immédiats', '6-12 mois'],
        ['Risques', 'Minimaux', 'Plus élevés (chirurgie)'],
      ],
      chooseTitle: 'Quand choisir chaque option?',
      rinomodelacion: 'Rhinomodelage: Idéal pour les corrections esthétiques qui ne nécessitent pas de réduire la taille du nez (bosses, asymétries, pointe tombante).',
      rinoplastia: 'Rhinoplastie: Nécessaire lorsqu\'il faut réduire la taille du nez ou corriger des problèmes fonctionnels.',
    },
    pt: {
      backText: 'Voltar para Perguntas Frequentes',
      ctaText: 'Mais perguntas? Contate-nos',
      titulo: 'Qual é a diferença entre Rinomodelação e Rinoplastia?',
      intro: 'Embora ambos os procedimentos busquem melhorar a aparência do nariz, são técnicas muito diferentes.',
      tableHeaders: ['Aspecto', 'Rinomodelação', 'Rinoplastia'],
      tableRows: [
        ['Tipo', 'Não cirúrgico', 'Cirúrgico'],
        ['Anestesia', 'Local tópica', 'Geral'],
        ['Duração', '15-30 minutos', '1-3 horas'],
        ['Recuperação', 'Imediata', '2-4 semanas'],
        ['Resultados', 'Imediatos', '6-12 meses'],
        ['Riscos', 'Mínimos', 'Maiores (cirurgia)'],
      ],
      chooseTitle: 'Quando escolher cada opção?',
      rinomodelacion: 'Rinomodelação: Ideal para correções estéticas que não requerem reduzir o tamanho do nariz (gibas, assimetrias, ponta caída).',
      rinoplastia: 'Rinoplastia: Necessária quando é preciso reduzir o tamanho do nariz ou corrigir problemas funcionais.',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <FAQPage 
      backText={t.backText}
      ctaText={t.ctaText}
      titulo={t.titulo}
      contenido={
        <div className="prose max-w-none">
          <p className="text-lg text-text-secondary leading-relaxed mb-6">
            {t.intro}
          </p>
          
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-cream">
                  {t.tableHeaders.map((header, index) => (
                    <th key={index} className="p-4 text-left text-[#1C1C1A] font-semibold border">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.tableRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 1 ? 'bg-cream' : ''}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className={`p-4 border ${cellIndex === 0 ? 'font-medium' : 'text-text-secondary'}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="bg-cream p-6 rounded-none">
            <h4 className="font-semibold text-[#1C1C1A] mb-3">{t.chooseTitle}</h4>
            <p className="text-text-secondary mb-3"><strong>{t.rinomodelacion}</strong></p>
            <p className="text-text-secondary"><strong>{t.rinoplastia}</strong></p>
          </div>
        </div>
      }
    />
  );
};

export const FAQDuracion = () => {
  const lang = useLanguage();
  
  const texts = {
    es: {
      backText: 'Volver a Preguntas Frecuentes',
      ctaText: '¿Más preguntas? Contáctanos',
      titulo: '¿Los resultados de la Rinomodelación son permanentes?',
      intro: 'La duración de los resultados depende del tipo de material utilizado. El Dr. Norro ofrece ambas opciones según las preferencias del paciente.',
      temporary: { title: 'Rinomodelación Temporal', duration: '12-18 meses', material: 'Con ácido hialurónico', features: ['Ideal para probar el resultado', 'Reversible si lo deseas', 'Puede repetirse periódicamente'] },
      permanent: { title: 'Rinomodelación Permanente', duration: 'Definitiva', material: 'Con hidroxiapatita cálcica', features: ['Resultados estables en el tiempo', 'Una sola intervención', 'Estimula colágeno propio'] },
      recommendationTitle: 'Recomendación del Dr. Norro:',
      recommendation: '"Muchos pacientes prefieren empezar con la opción temporal para ver cómo les queda y, una vez satisfechos con el resultado, optar por la versión permanente. Ambas opciones son seguras y efectivas."',
    },
    en: {
      backText: 'Back to FAQ',
      ctaText: 'More questions? Contact us',
      titulo: 'Are Rhinomodeling results permanent?',
      intro: 'The duration of results depends on the type of material used. Dr. Norro offers both options according to patient preferences.',
      temporary: { title: 'Temporary Rhinomodeling', duration: '12-18 months', material: 'With hyaluronic acid', features: ['Ideal for testing the result', 'Reversible if you wish', 'Can be repeated periodically'] },
      permanent: { title: 'Permanent Rhinomodeling', duration: 'Definitive', material: 'With calcium hydroxyapatite', features: ['Stable results over time', 'Single intervention', 'Stimulates own collagen'] },
      recommendationTitle: 'Dr. Norro\'s recommendation:',
      recommendation: '"Many patients prefer to start with the temporary option to see how it looks and, once satisfied with the result, opt for the permanent version. Both options are safe and effective."',
    },
    fr: {
      backText: 'Retour aux FAQ',
      ctaText: 'Plus de questions? Contactez-nous',
      titulo: 'Les résultats du Rhinomodelage sont-ils permanents?',
      intro: 'La durée des résultats dépend du type de matériau utilisé. Le Dr. Norro propose les deux options selon les préférences du patient.',
      temporary: { title: 'Rhinomodelage Temporaire', duration: '12-18 mois', material: 'Avec acide hyaluronique', features: ['Idéal pour tester le résultat', 'Réversible si vous le souhaitez', 'Peut être répété périodiquement'] },
      permanent: { title: 'Rhinomodelage Permanent', duration: 'Définitif', material: 'Avec hydroxyapatite de calcium', features: ['Résultats stables dans le temps', 'Une seule intervention', 'Stimule le collagène propre'] },
      recommendationTitle: 'Recommandation du Dr. Norro:',
      recommendation: '"De nombreux patients préfèrent commencer par l\'option temporaire pour voir le rendu et, une fois satisfaits du résultat, opter pour la version permanente. Les deux options sont sûres et efficaces."',
    },
    pt: {
      backText: 'Voltar para Perguntas Frequentes',
      ctaText: 'Mais perguntas? Contate-nos',
      titulo: 'Os resultados da Rinomodelação são permanentes?',
      intro: 'A duração dos resultados depende do tipo de material utilizado. O Dr. Norro oferece ambas as opções de acordo com as preferências do paciente.',
      temporary: { title: 'Rinomodelação Temporária', duration: '12-18 meses', material: 'Com ácido hialurônico', features: ['Ideal para testar o resultado', 'Reversível se desejar', 'Pode ser repetido periodicamente'] },
      permanent: { title: 'Rinomodelação Permanente', duration: 'Definitiva', material: 'Com hidroxiapatita de cálcio', features: ['Resultados estáveis no tempo', 'Uma única intervenção', 'Estimula colágeno próprio'] },
      recommendationTitle: 'Recomendação do Dr. Norro:',
      recommendation: '"Muitos pacientes preferem começar com a opção temporária para ver como fica e, uma vez satisfeitos com o resultado, optar pela versão permanente. Ambas as opções são seguras e eficazes."',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <FAQPage 
      backText={t.backText}
      ctaText={t.ctaText}
      titulo={t.titulo}
      contenido={
        <div className="prose max-w-none">
          <p className="text-lg text-text-secondary leading-relaxed mb-6">
            {t.intro}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-cream p-6 rounded-none">
              <h4 className="font-semibold text-[#1C1C1A] mb-3">{t.temporary.title}</h4>
              <p className="text-2xl font-light text-gold mb-2">{t.temporary.duration}</p>
              <p className="text-text-secondary mb-3">{t.temporary.material}</p>
              <ul className="space-y-2 text-sm text-text-secondary">
                {t.temporary.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle className="text-gold" size={16} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-cream p-6 rounded-none">
              <h4 className="font-semibold text-[#1C1C1A] mb-3">{t.permanent.title}</h4>
              <p className="text-2xl font-light text-gold mb-2">{t.permanent.duration}</p>
              <p className="text-text-secondary mb-3">{t.permanent.material}</p>
              <ul className="space-y-2 text-sm text-text-secondary">
                {t.permanent.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle className="text-blue-500" size={16} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="bg-cream p-6 rounded-none">
            <h4 className="font-semibold text-[#1C1C1A] mb-3">{t.recommendationTitle}</h4>
            <p className="text-text-secondary">{t.recommendation}</p>
          </div>
        </div>
      }
    />
  );
};

export default FAQPage;
